import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  bulkUploadDndAutoScroll,
  bulkUploadDndMeasuring,
  useBulkUploadDndSensors,
} from '../utils/bulkUploadDndConfig';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import Toast from '../../Toast';
import QuestionTooltip from '../../../components2/QuestionTooltip';
import helpIcon from '../../../static/img/24questionbordered.svg';
import docIcon from '../../../static/img/doc.svg';
import pdfIcon from '../../../static/img/pdf_logo.svg';
import imageIcon from '../../../static/img/no-image.svg';
import pagesEmptyIcon from '../../../static/img/bulk-pages-empty.svg';
import removeFileIcon from '../../../static/icons/red-cross.svg';

const UploadTabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="9.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M6.5 11.5V12.25C6.5 13.2165 7.2835 14 8.25 14H11.75C12.7165 14 13.5 13.2165 13.5 12.25V11.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 11.5V6.5M10 6.5L8.25 8.25M10 6.5L11.75 8.25"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TextTabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 6.75H18M12 6.75V17.25M8.5 17.25H15.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
import { BulkUploadWizardShell } from './BulkUploadWizardShell';
import { getPdfInfo, readConvertedWord } from '../utils/pdfUtils';
import QuestionRichTextEditor from '../../CreateNewTask/components/QuestionRichTextEditor';
import { uploadFileToServer } from '../../../service';
import {
  generateImageThumbnail,
  isPlaceholderThumbnail,
  imageThumbnailUrl,
  PLACEHOLDER_THUMBNAIL as DEFAULT_PAGE_THUMBNAIL,
} from '../utils/thumbnails';
import { htmlToPlainText } from '../utils/html';
import { getFileType } from '../utils/fileTypes';
// Shared with the /draftPages snapshot: pageNumber is a pure function of array
// position, and restoring a saved list has to number it the same way a drag does.
import { renumberPages } from '../utils/draftPageSnapshot';
import { useBulkFileUpload } from '../../../hooks/useBulkFileUpload';
import { FileDropArea } from './shared/FileDropArea';

import {
  BulkUploadTab,
  BulkUploadTabRow,
  BulkUploadHelperText,
  BulkUploadFilesSection,
  BulkUploadFilesHeading,
  BulkUploadFileList,
  BulkUploadFileRow,
  BulkUploadDragHandle,
  BulkUploadFileIcon,
  BulkUploadFileMeta,
  BulkUploadFileName,
  BulkUploadFilePages,
  BulkUploadFileDelete,
  BulkUploadFailedRow,
  BulkUploadFailedReason,
  BulkUploadRetryButton,
  BulkUploadSectionTitle,
} from '../styles';

// Additional styled components specific to this design
const DragDropLayout = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(201, 198, 204, 0.5);
  border-left: none;
  border-right: none;

  @media (max-width: 768px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 30px;
  overflow-y: auto;
  background: #ffffff;
  scrollbar-width: thin;
  scrollbar-color: #7200e0 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #7200e0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5b00b5;
  }

  @media (max-width: 768px) {
    padding: 16px;
    overflow-y: visible;
  }
`;

const PagesSidebar = styled.aside`
  width: 523px;
  flex-shrink: 0;
  border-left: 1px solid rgba(201, 198, 204, 0.5);
  padding: 24px 20px;
  overflow-y: auto;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scrollbar-width: thin;
  scrollbar-color: #7200e0 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #7200e0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5b00b5;
  }

  @media (max-width: 1200px) {
    width: 380px;
    padding: 20px 16px;
  }

  @media (max-width: 900px) {
    width: 320px;
    padding: 16px 12px;
  }

  @media (max-width: 768px) {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(201, 198, 204, 0.5);
    padding: 16px;
    overflow-y: visible;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const FieldLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 600;
  color: #1e252a;
  letter-spacing: -0.14px;

  span {
    font-weight: 400;
    color: #6b7280;
  }
`;

const FieldHelper = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #c9c6cc;
  border-radius: 8px;
  background: #ffffff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 1.5;
  color: #1e252a;

  &::placeholder {
    color: #918b97;
  }

  &:focus {
    outline: none;
    border-color: #7200e0;
    box-shadow: 0 0 0 2px rgba(114, 0, 224, 0.1);
  }
`;

const AddToPageButton = styled.button`
  width: 100%;
  height: 56px;
  box-sizing: border-box;
  padding: 16px 20px;
  border: 1.5px solid #7200e0;
  border-radius: 999px;
  background: #7200e0;
  color: #ffffff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:disabled {
    background: #c9c6cc;
    border-color: #c9c6cc;
    color: #fff;
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    background: #5b00b5;
    border-color: #5b00b5;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ManualTextEditorWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  margin-bottom: 16px;
  overflow: hidden;

  .question-quill-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    border-radius: 8px;
  }

  .question-quill-shell .ql-container.ql-snow {
    flex: 1;
    height: 0;
    overflow-y: auto;
    min-height: 0;
    border-radius: 0 0 7px 7px !important;
  }

  .ql-toolbar {
    justify-content: center !important;
    border-radius: 7px 7px 0 0 !important;
  }

  .ql-toolbar button,
  .ql-toolbar .ql-picker {
    margin-right: 8px !important;
  }

  .ql-editor.ql-blank::before {
    left: 15px !important;
    right: auto !important;
    text-align: left !important;
    padding: 0 !important;
    display: none !important;
    content: none !important;
  }

  .response-editor .ql-editor {
    text-align: left !important;
  }
`;

const PagesHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PagesTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const PagesTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
`;

const PagesTitleText = styled.h3`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #1e252a;
`;

const PagesOrderHint = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #667085;
`;

const HelpIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
  }
`;

const PageCountBadge = styled.span`
  min-width: 28px;
  padding: 0 7px;
  height: 28px;
  border-radius: 22px;
  background: #7200e0;
  color: #ffffff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PagesHelperText = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;
  color: #6b7280;
`;

const PagesEmptyState = styled.div`
  flex: 1;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  padding: 24px 16px;
`;

const PagesEmptyIcon = styled.img`
  width: 120px;
  height: auto;
`;

const PagesEmptyTitle = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 600;
  color: #7b7382;
`;

const PagesEmptySubtitle = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 400;
  color: #918b97;
`;

// The file list sits in a scrollable panel inside an overflow:hidden layout, so
// a dragged row is clipped at the panel edge. The overlay renders outside both,
// keeping the file visible while it is dragged onto the pages panel.
const DragFileOverlayCard = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 320px;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid #7200e0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(24, 24, 27, 0.18);
  cursor: grabbing;
`;

const PagesGridBase = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 141px);
  grid-auto-rows: min-content;
  gap: 20px;
  align-content: start;
  justify-content: start;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 141px);
    gap: 16px;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    justify-content: stretch;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PageCard = styled.div`
  width: 141px;
  background: #ffffff;
  border: ${({ $highlighted }) =>
    $highlighted
      ? '1.5px solid #7200e0'
      : '1px solid rgba(201, 198, 204, 0.8)'};
  border-radius: 8px;
  box-shadow: ${({ $highlighted }) =>
    $highlighted
      ? '0 4px 12px rgba(114, 0, 224, 0.18)'
      : '0 1px 4px rgba(0, 0, 0, 0.12)'};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PageCardTitle = styled.div`
  padding: 8px 8px 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #1e252a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PagePreviewContainer = styled.div`
  padding: 8px;
  display: flex;
  gap: 10px;
  position: relative;
`;

const PagePreview = styled.div`
  width: 125px;
  height: 173px;
  border: 1px solid rgba(201, 198, 204, 0.5);
  border-radius: 0;
  overflow: hidden;
  position: relative;
  background: #f5f5f5;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #ffffff;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    aspect-ratio: 125 / 173;
  }
`;

const ManualPagePreview = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px 8px;
  background: #ffffff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 7px;
  line-height: 1.35;
  color: #181718;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PageNumberBadge = styled.div`
  position: absolute;
  left: 8px;
  bottom: 0;
  min-width: 26px;
  height: 26px;
  background: #ffffff;
  border-right: 1px solid #c9c6cc;
  border-top: 1px solid #c9c6cc;
  border-radius: 0 4px 0 0;
  padding: 1px 8px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #000000;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const GripIcon = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden>
    <circle cx="2" cy="2" r="1.5" fill="currentColor" />
    <circle cx="8" cy="2" r="1.5" fill="currentColor" />
    <circle cx="2" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="2" cy="14" r="1.5" fill="currentColor" />
    <circle cx="8" cy="14" r="1.5" fill="currentColor" />
  </svg>
);

const SortableFileRow = ({ file, selected, onSelect, onRemove, sorting }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id, animateLayoutChanges: () => false });

  // The whole row is the drag activator: a 24px grip is too small a target to
  // drag a file across to the pages panel.
  const setRowRef = useCallback(
    (node) => {
      setNodeRef(node);
      setActivatorNodeRef(node);
    },
    [setNodeRef, setActivatorNodeRef],
  );

  return (
    <BulkUploadFileRow
      ref={setRowRef}
      style={{
        // Ignore transforms unless a file is being sorted. Page drags share this
        // DndContext and would otherwise pin rows in place after a reorder.
        transform: sorting && !isDragging ? CSS.Translate.toString(transform) : undefined,
        transition: sorting && !isDragging ? transition : undefined,
        opacity: isDragging ? 0.4 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      $selected={selected}
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <BulkUploadDragHandle aria-hidden="true">
        <GripIcon />
      </BulkUploadDragHandle>
      <BulkUploadFileIcon src={getFileIcon(file.type)} alt="" />
      <BulkUploadFileMeta>
        <BulkUploadFileName>{file.name}</BulkUploadFileName>
        <BulkUploadFilePages>
          {file.pageCount} {file.pageCount === 1 ? 'page' : 'pages'}
        </BulkUploadFilePages>
      </BulkUploadFileMeta>
      <BulkUploadFileDelete
        type="button"
        aria-label={`Remove ${file.name}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(file.id);
        }}
      >
        <img src={removeFileIcon} alt="" width={16} height={16} />
      </BulkUploadFileDelete>
    </BulkUploadFileRow>
  );
};

const FailedUploadRow = ({ upload, retrying, onRetry, onDismiss }) => (
  <BulkUploadFailedRow>
    <BulkUploadFileIcon src={getFileIcon(getFileType(upload.name))} alt="" />
    <BulkUploadFileMeta>
      <BulkUploadFileName>{upload.name}</BulkUploadFileName>
      <BulkUploadFailedReason>{upload.error}</BulkUploadFailedReason>
    </BulkUploadFileMeta>
    <BulkUploadRetryButton
      type="button"
      aria-label={`Retry ${upload.name}`}
      disabled={retrying}
      onClick={() => onRetry(upload)}
    >
      {retrying ? 'Retrying...' : 'Retry'}
    </BulkUploadRetryButton>
    <BulkUploadFileDelete
      type="button"
      aria-label={`Dismiss ${upload.name}`}
      disabled={retrying}
      onClick={() => onDismiss(upload.id)}
    >
      <img src={removeFileIcon} alt="" width={16} height={16} />
    </BulkUploadFileDelete>
  </BulkUploadFailedRow>
);

// Read-only preview. Page order follows the file list, and individual pages are
// rearranged in the Organise pages step, where student assignment is visible.
const PageCardBody = ({ page, index, highlighted }) => (
  <PageCard $highlighted={highlighted}>
    <PageCardTitle title={page.label || page.pageTitle || `Page ${index + 1}`}>
      {page.label || page.pageTitle || `Page ${index + 1}`}
    </PageCardTitle>
    <PagePreviewContainer>
      <PagePreview>
        {page.source === 'manual' && page.content ? (
          <ManualPagePreview>{htmlToPlainText(page.content)}</ManualPagePreview>
        ) : (
          <img
            src={page.thumbnail || page.fileUrl}
            alt={`Page ${page.pageNumber || index + 1}`}
            draggable={false}
          />
        )}
      </PagePreview>
      <PageNumberBadge>{index + 1}</PageNumberBadge>
    </PagePreviewContainer>
  </PageCard>
);

const readPageData = async (file) => {
  const type = getFileType(file.name);
  if (type === 'pdf') return await getPdfInfo(file);
  if (type === 'image')
    return { pageCount: 1, thumbnails: [await generateImageThumbnail(file)] };
  return { pageCount: 1, thumbnails: [DEFAULT_PAGE_THUMBNAIL] };
};

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.jpeg', '.jpg', '.png', '.heic', '.heif'];
const MIB = 1024 * 1024;
// JED-1387 M0 (06 §1.2): per-type caps instead of a flat 5 MB. uploadFileToServer
// already sends anything above the legacy multipart limit through the signed path.
const SIZE_CAPS = [
  { pattern: /\.pdf$/i, maxBytes: 100 * MIB },
  { pattern: /\.(jpe?g|png|heic|heif)$/i, maxBytes: 32 * MIB },
  { pattern: /\.docx$/i, maxBytes: 25 * MIB },
];
const MIN_FILE_SIZE_BYTES = 100; // Minimum 100 bytes to avoid empty/corrupt files

export const maxBytesFor = (file) =>
  SIZE_CAPS.find(({ pattern }) => pattern.test(file.name))?.maxBytes ?? 0;

const hasAllowedExtension = (file) =>
  ALLOWED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

export const partitionFiles = (files) => {
  const validFiles = [];
  const invalidFileNames = [];
  const oversizedFiles = [];
  const emptyFiles = [];

  files.forEach((file) => {
    if (!hasAllowedExtension(file)) {
      invalidFileNames.push(file.name);
    } else if (file.size < MIN_FILE_SIZE_BYTES) {
      emptyFiles.push(file.name);
    } else if (file.size > maxBytesFor(file)) {
      oversizedFiles.push({ name: file.name, limitMb: maxBytesFor(file) / MIB });
    } else {
      validFiles.push(file);
    }
  });

  return { validFiles, invalidFileNames, oversizedFiles, emptyFiles };
};

// These messages embed the teacher's file names: preserveCase, or the Toast's
// sentence-casing lowercases them and every word after the first
// ('Essay.PDF' → 'essay.pdf', '… Only .pdf' → '… only .pdf').
export const warnAboutInvalidFiles = (names) => {
  if (!names.length) return;
  toast(
    <Toast
      preserveCase
      message={`Invalid file type${names.length > 1 ? 's' : ''}: ${names.join(', ')}. Only .pdf, .docx, .jpeg, .png, and .heic/.heif files are allowed.`}
    />,
  );
};

export const oversizedMessage = (oversized) =>
  oversized.map(({ name, limitMb }) => `${name} is larger than ${limitMb} MB.`).join(' ');

export const warnAboutOversizedFiles = (oversized) => {
  if (!oversized.length) return;
  toast(<Toast preserveCase message={oversizedMessage(oversized)} />);
};

export const warnAboutEmptyFiles = (names) => {
  if (!names.length) return;
  toast(
    <Toast
      preserveCase
      message={`File${names.length > 1 ? 's' : ''} ${names.length > 1 ? 'are' : 'is'} empty or corrupt: ${names.join(', ')}. Please check the file${names.length > 1 ? 's' : ''} and try again.`}
    />,
  );
};

const buildFileItem = (file, fileUrl, pageData) => {
  const item = {
    // Not Date.now() + index: two drops inside the same millisecond then share
    // an id, and the dedupe-by-id pass on the page list silently deletes the
    // newer file's pages.
    id: `upload-${uuidv4()}`,
    name: file.name,
    type: getFileType(file.name),
    file,
    fileUrl,
    ...pageData,
  };
  // For images, prefer the generated data URL thumbnail from readPageData if it exists,
  // otherwise fall back to using the uploaded fileUrl. Data URL thumbnails are more
  // reliable for immediate display and work offline.
  if (item.type === 'image' && (!item.thumbnails || !item.thumbnails.length)) {
    item.thumbnails = [imageThumbnailUrl(fileUrl)];
  }
  return item;
};

const uploadOneFile = async (file) => {
  if (/\.docx$/i.test(file.name)) {
    const fileUrl = await uploadFileToServer(file);
    if (!fileUrl || /\.docx(?:[?#]|$)/i.test(fileUrl)) {
      throw new Error('The Word document could not be converted. Please try again.');
    }
    const { file: pdfFile, ...pageData } = await readConvertedWord(fileUrl, file.name);
    return { ...buildFileItem(pdfFile, fileUrl, pageData), name: file.name };
  }
  // Upload and thumbnailing are independent — thumbnails never need the URL —
  // so they overlap per file instead of running as two sequential phases.
  const [fileUrl, pageData] = await Promise.all([uploadFileToServer(file), readPageData(file)]);
  // uploadFileToServer resolves to null rather than throwing when every upload
  // path fails. A file with no URL cannot be referenced in a bulk draft, so its
  // pages would silently vanish from the submission — fail loudly here instead.
  if (!fileUrl) throw new Error('the upload did not complete');
  return buildFileItem(file, fileUrl, pageData);
};

const getFileIcon = (type) => {
  if (type === 'pdf') return pdfIcon;
  if (type === 'image') return imageIcon;
  return docIcon;
};

const normalizeFiles = (files = []) =>
  files.map((file, index) => {
    const name = file.name || `Uploaded file ${index + 1}`;
    return {
      ...file,
      id: file.id || `file-${index}-${name}`,
      name,
      type: file.type || getFileType(name),
      pageCount: file.pageCount || 1,
      thumbnails: file.thumbnails || [DEFAULT_PAGE_THUMBNAIL],
      file: file.file || file,
    };
  });

// Page order is derived from the file list, which is the only surface the user
// reorders. Manual entries carry a virtual file row, so a typed response sits
// wherever its row sits rather than being pinned to the end.
export const orderPagesByFiles = (fileItems, pageItems) =>
  fileItems.flatMap((file) => pageItems.filter((page) => page.fileId === file.id));

const reorderById = (items, activeId, overId) => {
  if (!overId || activeId === overId) return null;
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  return from < 0 || to < 0 ? null : arrayMove(items, from, to);
};

const buildPagesForFiles = (fileItems) =>
  fileItems
    .filter((file) => file.type !== 'manual' && file.source !== 'manual')
    .flatMap((file) => {
      const pageCount = Math.max(1, Number(file.pageCount) || 1);
      const thumbnails = Array.isArray(file.thumbnails) ? file.thumbnails : [];
      return Array.from({ length: pageCount }, (_, index) => ({
        id: `${file.id}-p${index + 1}`,
        fileId: file.id,
        sourcePageIndex: index,
        pageNumber: 0,
        label:
          file.name?.replace(/\.(docx?|pdf|jpe?g|png|heic|heif)$/i, '') || 'Page',
        thumbnail: thumbnails[index] || DEFAULT_PAGE_THUMBNAIL,
        source: 'file',
        file: file.file,  // ✅ CRITICAL: Preserve File instance for upload
        fileUrl: file.fileUrl,  // ✅ Store uploaded file URL for text extraction
      }));
    });

const pagesNeedPdfRefresh = (fileItems, currentPages) =>
  fileItems.some((file) => {
    if (!file.thumbnails?.length) return false;
    const hasRealThumbs = file.thumbnails.some((t) => !isPlaceholderThumbnail(t));
    if (!hasRealThumbs) return false;
    const filePages = currentPages.filter((page) => page.fileId === file.id);
    // Fewer pages than pageCount is intentional (user deleted pages) — only
    // refresh when existing page thumbnails are still placeholders.
    if (filePages.length === 0) return false;
    return filePages.some((page) => isPlaceholderThumbnail(page.thumbnail));
  });

export const BulkUploadDragDropStep = ({
  currentStep = 2,
  steps,
  mode = 'schoolTeacher',
  files = [],
  pages = [],
  onFilesSelect,
  onPagesChange,
  onContinue,
  onBack,
  onClose,
  continueLabel = 'Continue',
  isContinueDisabled = false,
  fullPage = false,
  onProcessingChange,
  onUnsavedTextChange,
}) => {
  const inputRef = useRef(null);
  const pagesSidebarRef = useRef(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [pageTitle, setPageTitle] = useState('');
  const [manualText, setManualText] = useState('');
  React.useLayoutEffect(() => {
    onUnsavedTextChange?.(Boolean(manualText.trim() || pageTitle.trim()));
    return () => onUnsavedTextChange?.(false);
  }, [manualText, pageTitle, onUnsavedTextChange]);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const isProcessingFiles = pendingUploadCount > 0;
  React.useLayoutEffect(() => {
    onProcessingChange?.(isProcessingFiles);
    return () => onProcessingChange?.(false);
  }, [isProcessingFiles, onProcessingChange]);
  const [retryingUploadIds, setRetryingUploadIds] = useState(new Set());
  const retryingUploadIdsRef = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const { uploadAll, retryUpload, dismissUpload, failedUploads } = useBulkFileUpload({
    uploadFile: uploadOneFile,
  });

  const updatePages = useCallback(
    (updater) => {
      if (!onPagesChange) return;
      onPagesChange((current) => {
        const prev = Array.isArray(current) ? current : [];
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (next === prev) return prev;
        return renumberPages(next);
      });
    },
    [onPagesChange],
  );

  const uploadedFiles = useMemo(() => normalizeFiles(files), [files]);
  const currentFilesRef = useRef(uploadedFiles);
  currentFilesRef.current = uploadedFiles;
  const commitFiles = useCallback((next) => {
    // Upload completions retain the callback from before the request began.
    // Publish the current list synchronously so typed additions, deletions and
    // other completed uploads survive even when React batches the updates.
    currentFilesRef.current = next;
    onFilesSelect?.(next);
  }, [onFilesSelect]);
  // File order is authoritative — nothing downstream of it may reorder pages.
  const displayedFiles = uploadedFiles;
  const activeFileId = selectedFileId || displayedFiles[0]?.id || null;
  const highlightedPageIds = useMemo(() => {
    if (!activeFileId) return new Set();
    return new Set(
      pages.filter((page) => page.fileId === activeFileId).map((page) => page.id),
    );
  }, [activeFileId, pages]);

  const [draggingFileId, setDraggingFileId] = useState(null);
  const isDraggingRef = useRef(false);
  const activeDragFile = useMemo(
    () => displayedFiles.find((file) => file.id === draggingFileId) || null,
    [draggingFileId, displayedFiles],
  );
  const isFileSortActive = !!activeDragFile;

  const sensors = useBulkUploadDndSensors();

  const prevFilesLenRef = useRef(0);
  useEffect(() => {
    const prevLen = prevFilesLenRef.current;
    prevFilesLenRef.current = uploadedFiles.length;
    if (pages.length > 0 || !uploadedFiles.length || !onPagesChange) return;
    // Seed pages when files first appear or grow — do not recreate after the
    // user deletes every page while files remain.
    if (prevLen === 0 || uploadedFiles.length > prevLen) {
      onPagesChange(renumberPages(buildPagesForFiles(uploadedFiles)));
    }
  }, [uploadedFiles, pages.length, onPagesChange]);

  // Keep sidebar pages in sync with extracted PDF/image thumbnails.
  useEffect(() => {
    if (!uploadedFiles.length || !onPagesChange || isDraggingRef.current) return;
    updatePages((prev) => {
      if (!pagesNeedPdfRefresh(uploadedFiles, prev)) return prev;

      // Only update thumbnails for pages that already exist — never re-add
      // pages the user deleted.
      const allFilePages = buildPagesForFiles(uploadedFiles);
      return prev.map((page) => {
        if (page.source === 'manual') return page;
        const refreshedPage = allFilePages.find((p) => p.id === page.id);
        return refreshedPage
          ? {
              ...page,
              thumbnail: refreshedPage.thumbnail,
              sourcePageIndex: refreshedPage.sourcePageIndex,
            }
          : page;
      });
    });
  }, [uploadedFiles, onPagesChange, updatePages]);

  const scrollPagesToEnd = () =>
    requestAnimationFrame(() =>
      pagesSidebarRef.current?.scrollTo({
        top: pagesSidebarRef.current.scrollHeight,
        behavior: 'smooth',
      }),
    );

  const appendPagesForFiles = (incoming, orderedFiles) =>
    // Keep the pages already on screen — rebuilding them from files would
    // resurrect pages the user deleted — and append only the new files'.
    updatePages((prev) =>
      orderPagesByFiles(
        orderedFiles,
        [...prev, ...buildPagesForFiles(incoming)],
      ),
    );

  const appendFiles = (incoming) => {
    if (!mounted.current || !incoming.length) return;
    const nextFiles = [...currentFilesRef.current, ...incoming];
    commitFiles(nextFiles);
    appendPagesForFiles(incoming, nextFiles);
    setSelectedFileId(incoming[0].id);
    scrollPagesToEnd();
  };

  // One bad file must not discard the rest of the drop, so the uploads settle
  // independently: whatever landed is kept and the rest come back as rows the
  // teacher can retry without re-dropping the batch.
  const uploadAndAppend = async (files) => {
    setPendingUploadCount((count) => count + 1);
    try {
      appendFiles(await uploadAll(files));
    } finally {
      if (mounted.current) setPendingUploadCount((count) => count - 1);
    }
  };

  const addFiles = async (fileList) => {
    const { validFiles, invalidFileNames, oversizedFiles, emptyFiles } = partitionFiles(Array.from(fileList || []));
    warnAboutInvalidFiles(invalidFileNames);
    warnAboutOversizedFiles(oversizedFiles);
    warnAboutEmptyFiles(emptyFiles);
    if (validFiles.length) await uploadAndAppend(validFiles);
  };

  const retryFailedUpload = async (upload) => {
    if (retryingUploadIdsRef.current.has(upload.id)) return;
    retryingUploadIdsRef.current.add(upload.id);
    setRetryingUploadIds(new Set(retryingUploadIdsRef.current));
    setPendingUploadCount((count) => count + 1);
    try {
      const uploaded = await retryUpload(upload);
      if (uploaded) appendFiles([uploaded]);
    } finally {
      retryingUploadIdsRef.current.delete(upload.id);
      if (mounted.current) {
        setRetryingUploadIds(new Set(retryingUploadIdsRef.current));
        setPendingUploadCount((count) => count - 1);
      }
    }
  };

  const removeFile = (fileId) => {
    const next = currentFilesRef.current.filter((file) => file.id !== fileId);
    commitFiles(next);
    updatePages((prev) => prev.filter((page) => page.fileId !== fileId));
    if (selectedFileId === fileId) setSelectedFileId(next[0]?.id || null);
  };

  const addManualPage = () => {
    if (!manualText.trim()) return;

    const trimmedTitle = pageTitle.trim();

    // Check for duplicate titles (case-sensitive) only if title is non-empty
    if (trimmedTitle) {
      const isDuplicate = pages.some((page) => page.label === trimmedTitle);
      if (isDuplicate) {
        toast(
          <Toast message={`A page with the title "${trimmedTitle}" already exists. Please use a unique title.`} />
        );
        return;
      }
    }

    const fileId = `manual-file-${Date.now()}`;
    const pageId = `manual-${Date.now()}`;
    const label = trimmedTitle || 'Manual response';

    // Create a virtual file entry for the manual page
    const virtualFile = {
      id: fileId,
      name: label,
      type: 'manual',
      pageCount: 1,
      thumbnails: [DEFAULT_PAGE_THUMBNAIL],
      file: null,
      source: 'manual',
    };

    const newPage = {
      id: pageId,
      fileId: fileId,
      source: 'manual',
      sourcePageIndex: 0,
      pageNumber: 0,
      label: label,
      thumbnail: DEFAULT_PAGE_THUMBNAIL,
      content: manualText.trim(),
      pageTitle: trimmedTitle,
    };

    // Add virtual file to files list
    commitFiles([...currentFilesRef.current, virtualFile]);

    // Add page to pages list
    updatePages((prev) => [...prev, newPage]);

    setManualText('');
    setPageTitle('');
    requestAnimationFrame(() => {
      pagesSidebarRef.current?.scrollTo({
        top: pagesSidebarRef.current.scrollHeight,
        behavior: 'smooth',
      });
    });
  };

  const handleDeletePage = useCallback((pageId) => {
    const pageToDelete = pages.find((p) => p.id === pageId);
    if (!pageToDelete) return;

    const nextPages = pages.filter((page) => page.id !== pageId);
    updatePages(() => nextPages);

    if (!pageToDelete.fileId || !onFilesSelect) return;

    const remainingFromFile = nextPages.filter(
      (p) => p.fileId === pageToDelete.fileId,
    );

    if (remainingFromFile.length === 0) {
      commitFiles(currentFilesRef.current.filter((f) => f.id !== pageToDelete.fileId));
      return;
    }

    commitFiles(
      currentFilesRef.current.map((file) =>
        file.id === pageToDelete.fileId
          ? { ...file, pageCount: remainingFromFile.length }
          : file,
      ),
    );
  }, [updatePages, pages, commitFiles, onFilesSelect]);

  const handleFileDragStart = useCallback(({ active }) => {
    isDraggingRef.current = true;
    setDraggingFileId(active.id);
  }, []);

  const handleFileDragCancel = useCallback(() => {
    isDraggingRef.current = false;
    setDraggingFileId(null);
  }, []);

  const applyFileOrder = useCallback(
    (reorderedFiles) => {
      commitFiles(reorderedFiles);
      updatePages((prev) => orderPagesByFiles(reorderedFiles, prev));
    },
    [commitFiles, updatePages],
  );

  const handleFileDragEnd = useCallback(
    ({ active, over }) => {
      handleFileDragCancel();
      const reordered = reorderById(displayedFiles, active?.id, over?.id);
      if (reordered) applyFileOrder(reordered);
    },
    [displayedFiles, applyFileOrder, handleFileDragCancel],
  );

  const disabled = isContinueDisabled || pages.length === 0 || isProcessingFiles;

  // Test data attributes
  const pdfCount = uploadedFiles.filter(f => f.type === 'pdf').length;
  const imageCount = uploadedFiles.filter(f => f.type === 'image').length;
  const manualCount = pages.filter(p => p.source === 'manual').length;

  return (
    <BulkUploadWizardShell
      currentStep={currentStep}
      steps={steps}
      onBack={onBack}
      isBackDisabled={isProcessingFiles}
      onClose={onClose}
      onContinue={onContinue}
      continueLabel={continueLabel}
      isContinueDisabled={disabled}
      fullPage={fullPage}
      data-component="bulk-upload-dragdrop"
      data-unit="bulk-upload-step-1"
      data-total-files={uploadedFiles.length}
      data-total-pages={pages.length}
      data-pdf-count={pdfCount}
      data-image-count={imageCount}
      data-manual-count={manualCount}
      data-is-processing={isProcessingFiles}
      data-continue-disabled={disabled}
      data-active-tab={activeTab}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={bulkUploadDndMeasuring}
        autoScroll={bulkUploadDndAutoScroll}
        onDragStart={handleFileDragStart}
        onDragCancel={handleFileDragCancel}
        onDragEnd={handleFileDragEnd}
      >
        <DragDropLayout>
          <MainContent
          onDragOver={(e) => {
            if (activeTab === 'upload') {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }
          }}
          onDragEnter={(e) => {
            if (activeTab === 'upload') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onDragLeave={(e) => {
            if (activeTab === 'upload') {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }
          }}
          onDrop={(e) => {
            if (activeTab === 'upload') {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              addFiles(e.dataTransfer.files);
            }
          }}
        >
          <BulkUploadSectionTitle>Add responses</BulkUploadSectionTitle>

          <BulkUploadTabRow>
            <BulkUploadTab
              type="button"
              $active={activeTab === 'upload'}
              onClick={() => setActiveTab('upload')}
            >
              <UploadTabIcon />
              Upload
            </BulkUploadTab>
            <BulkUploadTab
              type="button"
              $active={activeTab === 'manual'}
              onClick={() => setActiveTab('manual')}
            >
              <TextTabIcon />
              Manually add text
            </BulkUploadTab>
          </BulkUploadTabRow>

          <BulkUploadHelperText>
            {activeTab === 'upload'
              ? 'Upload typed or handwritten responses, including photos (.jpeg, .png, .heic/.heif), PDF files, and Word documents (.docx)'
              : 'Type or paste student responses to add them as pages'}
          </BulkUploadHelperText>

          {activeTab === 'upload' ? (
            <>
              <FileDropArea
                inputRef={inputRef}
                hasFiles={uploadedFiles.length > 0}
                isDragging={isDragging}
                onDraggingChange={setIsDragging}
                onFiles={addFiles}
                isProcessing={isProcessingFiles}
                processingHint="Extracting pages from PDF files..."
                fileSection={
                  <BulkUploadFilesSection>
                    <BulkUploadFilesHeading>
                      Uploaded files
                      {isProcessingFiles && (
                        <span
                          style={{
                            marginLeft: 8,
                            color: '#7200e0',
                            fontSize: 13,
                            fontWeight: 400,
                          }}
                        >
                          (Processing...)
                        </span>
                      )}
                    </BulkUploadFilesHeading>
                    <BulkUploadFileList>
                      <SortableContext
                        items={displayedFiles.map((file) => file.id)}
                        strategy={verticalListSortingStrategy}
                        id="uploaded-files"
                      >
                        {displayedFiles.map((file) => (
                          <SortableFileRow
                            key={file.id}
                            file={file}
                            selected={file.id === activeFileId}
                            sorting={isFileSortActive}
                            onSelect={() => setSelectedFileId(file.id)}
                            onRemove={removeFile}
                          />
                        ))}
                      </SortableContext>
                    </BulkUploadFileList>
                  </BulkUploadFilesSection>
                }
                failedSection={
                  failedUploads.length > 0 && (
                    <BulkUploadFilesSection>
                      <BulkUploadFilesHeading>Failed uploads</BulkUploadFilesHeading>
                      <BulkUploadFileList>
                        {failedUploads.map((upload) => (
                          <FailedUploadRow
                            key={upload.id}
                            upload={upload}
                            retrying={retryingUploadIds.has(upload.id)}
                            onRetry={retryFailedUpload}
                            onDismiss={dismissUpload}
                          />
                        ))}
                      </BulkUploadFileList>
                    </BulkUploadFilesSection>
                  )
                }
              />
            </>
          ) : (
            <>
              <FieldGroup>
                <FieldLabelGroup>
                  <FieldLabel>
                    Response name <span>(optional)</span>
                  </FieldLabel>
                  <FieldHelper>
                    Add response name to easily identify this page
                  </FieldHelper>
                </FieldLabelGroup>
                <TextInput
                  type="text"
                  placeholder="Page Title"
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                />
              </FieldGroup>

              <FieldGroup style={{ flex: 1, minHeight: 0, display: 'flex' }}>
                <FieldLabel>Response</FieldLabel>
                <ManualTextEditorWrapper>
                  <QuestionRichTextEditor
                    value={manualText}
                    onChange={setManualText}
                    placeholder="Type your response here..."
                    hasFooter={false}
                    toolbarVariant="response"
                  />
                </ManualTextEditorWrapper>
                <AddToPageButton
                  type="button"
                  disabled={!manualText.trim()}
                  onClick={addManualPage}
                >
                  Add to pages
                  <span style={{ fontSize: '24px', lineHeight: '24px' }}>+</span>
                </AddToPageButton>
              </FieldGroup>
            </>
          )}
        </MainContent>

        <PagesSidebar ref={pagesSidebarRef}>
          <PagesHeader>
            <PagesTopRow>
              <PagesTitle>
                <PagesTitleText>Pages</PagesTitleText>
                <QuestionTooltip text="You can assign these pages to students in the next step">
                  <HelpIcon>
                    <img src={helpIcon} alt="Help" />
                  </HelpIcon>
                </QuestionTooltip>
              </PagesTitle>
              <PageCountBadge>{pages.length}</PageCountBadge>
            </PagesTopRow>
            <PagesOrderHint>
              Page order follows your file list. You can rearrange individual
              pages in the next step.
            </PagesOrderHint>
          </PagesHeader>

          {pages.length === 0 ? (
              <PagesEmptyState>
                <PagesEmptyIcon src={pagesEmptyIcon} alt="" />
                <div>
                  <PagesEmptyTitle>No pages added</PagesEmptyTitle>
                  <PagesEmptySubtitle>
                    Upload files or add text manually
                  </PagesEmptySubtitle>
                </div>
              </PagesEmptyState>
            ) : (
              <PagesGridBase>
                {pages.map((page, index) => (
                  <PageCardBody
                    key={page.id}
                    page={page}
                    index={index}
                    highlighted={highlightedPageIds.has(page.id)}
                  />
                ))}
              </PagesGridBase>
            )}
          </PagesSidebar>
        </DragDropLayout>
        <DragOverlay adjustScale={false} dropAnimation={null}>
          {activeDragFile && (
            <DragFileOverlayCard>
              <BulkUploadFileIcon src={getFileIcon(activeDragFile.type)} alt="" />
              <BulkUploadFileMeta>
                <BulkUploadFileName>{activeDragFile.name}</BulkUploadFileName>
                <BulkUploadFilePages>
                  {activeDragFile.pageCount}{' '}
                  {activeDragFile.pageCount === 1 ? 'page' : 'pages'}
                </BulkUploadFilePages>
              </BulkUploadFileMeta>
            </DragFileOverlayCard>
          )}
        </DragOverlay>
      </DndContext>
    </BulkUploadWizardShell>
  );
};
